/**
 * Copyright 2025 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {WasmModule} from '@litertjs/wasm-utils';

import {CompiledModel} from './compiled_model';
import {Environment} from './environment';
import {getGlobalLiteRt} from './global_litert';
import {readableStreamDefaultReaderToUint8Array, urlToUint8Array} from './load_utils';
import {Model} from './model';
import {CompileOptions, fillCompileOptions} from './model_types';
import {Deletable, LiteRtWasm} from './wasm_binding_types';
import {isJspiSupported} from './wasm_feature_detect';

/**
 * Check if the browser supports WebGPU.
 */
export function isWebGPUSupported(): boolean {
  return !!(
      typeof globalThis !== 'undefined' && (globalThis.navigator) &&
      (globalThis.navigator.gpu));
}

// These functions wrap the global LiteRT instance's methods and make it easier
// to call them.

/**
 * Get or create the default environment.
 */
export function getDefaultEnvironment() {
  return getGlobalLiteRt().getDefaultEnvironment();
}

/**
 * Loads and compiles a LiteRt model.
 *
 * @param model The model data. This can be a string (the model url), a URL
 *     object, a Uint8Array (the model bytes), or a
 *     ReadableStreamDefaultReader (for streaming model loading).
 * @param compileOptions The options for compiling the model. This includes
 *     the accelerator to use ('webgpu' or 'wasm') and the WebGPU device
 *     (for direct GPU model inputs / outputs).
 * @return A promise that resolves to the CompiledModel.
 */
export function loadAndCompile(
    model: string|URL|Uint8Array|ReadableStreamDefaultReader,
    compileOptions?: CompileOptions,
    ): Promise<CompiledModel> {
  return getGlobalLiteRt().loadAndCompile(model, compileOptions);
}

/**
 * Get the WebGPU device used by the default environment.
 */
export function getWebGpuDevice(): GPUDevice|null {
  return getGlobalLiteRt().getWebGpuDevice();
}

/**
 * Create a new default environment with the given WebGPU device.
 */
export function setWebGpuDevice(device: GPUDevice) {
  getGlobalLiteRt().setWebGpuDevice(device);
}

/**
 * Run LiteRt models in the browser.
 */
export class LiteRt {
  readonly liteRtWasm: LiteRtWasm;
  private defaultEnvironment?: Environment;
  private readonly objectsToDelete = new Set<Deletable>();

  constructor(wasmModule: WasmModule) {
    this.liteRtWasm = wasmModule as LiteRtWasm;
    this.liteRtWasm.setupLogging();
  }

  setDefaultEnvironment(environment: Environment) {
    this.defaultEnvironment = environment;
  }

  getDefaultEnvironment(): Environment {
    if (!this.defaultEnvironment) {
      throw new Error('Default environment is not set.');
    }
    return this.defaultEnvironment;
  }

  setWebGpuDevice(device: GPUDevice) {
    // Environment is immutable, so we need to create a new one.
    const oldEnvironment = this.getDefaultEnvironment();
    this.setDefaultEnvironment(new Environment({
      ...oldEnvironment.options,
      webGpuDevice: device,
    }));
  }

  getWebGpuDevice(): GPUDevice|null {
    return this.getDefaultEnvironment().webGpuDevice;
  }

  /**
   * Registers an object to be deleted when this LiteRt instance is deleted.
   * Internal use only.
   */
  _registerObjectForDeletion(object: Deletable) {
    this.objectsToDelete.add(object);
  }

  /**
   * Unregisters an object from being deleted when this LiteRt instance is
   * deleted. Internal use only.
   */
  _unregisterObjectForDeletion(object: Deletable) {
    this.objectsToDelete.delete(object);
  }

  /**
   * Loads and compiles a LiteRt model.
   *
   * @param model The model data. This can be a string (the model url), a URL
   *     object, a Uint8Array (the model bytes), or a
   *     ReadableStreamDefaultReader (for streaming model loading).
   * @param compileOptions The options for compiling the model. This includes
   *     the accelerator to use ('webgpu' or 'wasm') and the WebGPU device
   *     (for direct GPU model inputs / outputs).
   * @returns A promise that resolves to the CompiledModel.
   */
  async loadAndCompile(
      model: string|URL|Uint8Array|ReadableStreamDefaultReader<Uint8Array>,
      compileOptions: CompileOptions = {}): Promise<CompiledModel> {
    let modelData: Uint8Array;
    if (typeof model === 'string' || model instanceof URL) {
      modelData = await urlToUint8Array(model);
    } else if (model instanceof Uint8Array) {
      modelData = model;
    } else if (model instanceof ReadableStreamDefaultReader) {
      modelData = await readableStreamDefaultReaderToUint8Array(model);
    } else {
      throw new Error('Unsupported model type.');
    }

    // Assign defaults
    const environment =
        compileOptions.environment ?? this.getDefaultEnvironment();
    const accelerator = compileOptions.accelerator ??
        (environment.webGpuDevice ? 'webgpu' : 'wasm');

    const isWebGpu = accelerator === 'webgpu';

    if (isWebGpu && !environment.webGpuDevice) {
      throw new Error(
          'WebGPU was requested but no WebGPU device is set in the ' +
          'environment.');
    }

    const filledCompileOptions = fillCompileOptions(
        compileOptions, environment, this.liteRtWasm.getThreadCount());

    const ptr = this.liteRtWasm._malloc(modelData.byteLength);
    this.liteRtWasm.HEAPU8.set(modelData, ptr);
    const wasmModel = this.liteRtWasm.loadModel(
        filledCompileOptions.environment.liteRtEnvironment, ptr,
        modelData.byteLength);
    const wasmCompiledModel = await this.liteRtWasm.compileModel(
        filledCompileOptions.environment.liteRtEnvironment, wasmModel,
        filledCompileOptions);
    const loadedModel = new Model(wasmModel, () => {
      this.liteRtWasm._free(ptr);
    });
    const compiledModel = new CompiledModel(
        loadedModel, wasmCompiledModel, filledCompileOptions, () => {
          // When deleted, remove the compiled model from the set of objects to
          // delete.
          this.objectsToDelete.delete(compiledModel);
        });

    // Currently, compiledModel will delete the loadedModel when it is deleted.
    this.objectsToDelete.add(compiledModel);

    const isWebNn = accelerator === 'webnn';
    const acceleratorRequested = isWebGpu || isWebNn;

    if (acceleratorRequested && !compiledModel.isFullyAccelerated) {
      if (isJspiSupported()) {
        console.warn(
            `%c[LiteRT]%c Model not fully compiled for ${accelerator}. ` +
            'Partially delegating to WASM execution.',
            'background: #FFA000; color: black; font-weight: bold; padding: 2px 5px; border-radius: 3px;',
            'font-weight: bold;'
        );
      } else {
        console.warn(
            `%c[LiteRT]%c Model not fully compiled for ${accelerator} on non-JSPI browser. ` +
            'Falling back to WASM execution.',
            'background: #D32F2F; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;',
            'color: #D32F2F; font-weight: bold;'
        );
        compiledModel.delete();
        const fallbackCompileOptions = {
          ...compileOptions,
          accelerator: 'wasm' as const,
        };
        return this.loadAndCompile(modelData, fallbackCompileOptions);
      }
    }

    return compiledModel;
  }

  delete() {
    for (const object of this.objectsToDelete) {
      object.delete();
    }
  }
}
